import React, { useEffect, useState } from 'react';
import { Button } from '@chakra-ui/react';
import axios from 'axios';
import AutoComplete from 'react-select';
import { Tooltip, IconButton } from '@mui/material';
import { TbRefresh } from 'react-icons/tb';
import { MdOutlineContentCopy, MdInfoOutline } from 'react-icons/md';
import InputField from 'components/fields/InputField';
import { SubaccountData } from '../types';
import FeeGroupSelector from '../components/FeeGroupSelector';
import PaymentMethodsSelector from '../components/PaymentMethodsSelector';

interface SubaccountsStepProps {
  subaccounts: SubaccountData[];
  onAdd: () => void;
  onRemove: (tempId: string) => void;
  onUpdate: (tempId: string, data: Partial<SubaccountData>) => void;
  isProcessing?: boolean;
}

interface DialcodeOption {
  value: string;
  label: string;
}

const SubaccountsStep: React.FC<SubaccountsStepProps> = ({
  subaccounts,
  onAdd,
  onRemove,
  onUpdate,
  isProcessing = false,
}) => {
  const [dialcodeOptions, setDialcodeOptions] = useState<DialcodeOption[]>([]);
  const [isDialcodeLoading, setIsDialcodeLoading] = useState(true);
  const [postbackUrlErrors, setPostbackUrlErrors] = useState<Record<string, string>>({});
  const [emailErrors, setEmailErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchCountries();
  }, []);

  // Auto-generate API keys for new subaccounts
  useEffect(() => {
    subaccounts.forEach(sub => {
      if (!sub.auth1 || !sub.auth2) {
        const updates: Partial<SubaccountData> = {};
        if (!sub.auth1) updates.auth1 = generateAPIKey();
        if (!sub.auth2) updates.auth2 = generateAPISecret();
        if (Object.keys(updates).length > 0) {
          onUpdate(sub.tempId, updates);
        }
      }
    });
  }, [subaccounts.length]);

  const fetchCountries = async () => {
    try {
      setIsDialcodeLoading(true);
      const response = await axios.get(
        process.env.REACT_APP_API_URL + `/api/datasource/countries`
      );

      const countries = response?.data || [];
      const dialcodeMap: Record<string, string> = {};

      countries.forEach((c: any) => {
        if (c.dialcode !== null) {
          if (c.dialcode in dialcodeMap) {
            dialcodeMap[c.dialcode] = dialcodeMap[c.dialcode] + ', ' + c.country_name;
          } else {
            dialcodeMap[c.dialcode] = '(+' + c.dialcode + ') ' + c.country_name;
          }
        }
      });

      const options = Object.keys(dialcodeMap)
        .sort((a, b) => parseInt(a) - parseInt(b))
        .map(key => ({
          value: key,
          label: dialcodeMap[key],
        }));

      setDialcodeOptions(options);
    } catch (error) {
      console.error('Error fetching countries', error);
    } finally {
      setIsDialcodeLoading(false);
    }
  };

  const generateAPIKey = (): string => {
    const array = new Uint8Array(24);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  };

  const generateAPISecret = (): string => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  };

  const handleClickCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const validatePostbackUrl = (url: string): boolean => {
    if (url === '') return true;
    const urlPattern = /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,6})(\/[\w.-]*)*\/?(\?.*)?(#.*)?$/i;
    return urlPattern.test(url);
  };

  const validateEmail = (email: string): boolean => {
    if (email === '') return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handlePostbackUrlChange = (tempId: string, value: string) => {
    onUpdate(tempId, { postbackUrl: value });
    if (!validatePostbackUrl(value)) {
      setPostbackUrlErrors(prev => ({
        ...prev,
        [tempId]: 'Invalid URL format. Please enter a valid URL, e.g. https://example.com',
      }));
    } else {
      setPostbackUrlErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[tempId];
        return newErrors;
      });
    }
  };

  const handleEmailChange = (tempId: string, value: string) => {
    onUpdate(tempId, { email: value });
    if (!validateEmail(value)) {
      setEmailErrors(prev => ({
        ...prev,
        [tempId]: 'Invalid email format',
      }));
    } else {
      setEmailErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[tempId];
        return newErrors;
      });
    }
  };

  const handleMobileChange = (tempId: string, value: string) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    onUpdate(tempId, { mobile: numericValue });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Subaccounts</h3>
          <p className="mt-1 text-sm text-gray-500">
            Configure subaccounts with their fee groups and payment methods.
          </p>
        </div>
        <Button
          onClick={onAdd}
          disabled={isProcessing}
          className="btn btn-default flex items-center gap-2"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Subaccount
        </Button>
      </div>

      {subaccounts.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-300 p-12 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
          <h3 className="mt-4 text-sm font-medium text-gray-900">No subaccounts</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by adding a new subaccount.
          </p>
          <Button
            onClick={onAdd}
            disabled={isProcessing}
            className="btn btn-primary mt-4"
          >
            Add Subaccount
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {subaccounts.map((subaccount, index) => (
            <div
              key={subaccount.tempId}
              className="relative rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
            >
              {/* Header */}
              <div className="mb-6 flex items-center justify-between border-b border-gray-100 pb-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-600">
                    {index + 1}
                  </span>
                  <h4 className="text-base font-semibold text-gray-900">
                    {subaccount.name || `Subaccount ${index + 1}`}
                  </h4>
                </div>
                {subaccounts.length > 1 && (
                  <button
                    type="button"
                    onClick={() => onRemove(subaccount.tempId)}
                    disabled={isProcessing}
                    className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                )}
              </div>

              {/* Account Information Section */}
              <div className="mb-6 space-y-4 border-b border-gray-200 pb-6">
                <p className="text-md font-medium tracking-normal text-gray-700">
                  Account Information
                </p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <InputField
                    disabled={isProcessing}
                    variant="auth"
                    extra="shadow-sm"
                    label="Subaccount Name"
                    required={true}
                    placeholder="Enter subaccount name"
                    id={`suba_name_${subaccount.tempId}`}
                    value={subaccount.name}
                    type="text"
                    autoComplete="off"
                    onChange={e => onUpdate(subaccount.tempId, { name: e.target.value })}
                  />

                  <InputField
                    disabled={isProcessing}
                    variant="auth"
                    extra="shadow-sm"
                    label="Postback URL"
                    required={true}
                    placeholder="https://example.com"
                    id={`postback_url_${subaccount.tempId}`}
                    value={subaccount.postbackUrl}
                    type="text"
                    autoComplete="off"
                    onChange={e => handlePostbackUrlChange(subaccount.tempId, e.target.value)}
                    error={postbackUrlErrors[subaccount.tempId]}
                  />
                </div>
              </div>

              {/* Personal Information Section */}
              <div className="mb-6 space-y-4 border-b border-gray-200 pb-6">
                <p className="text-md font-medium tracking-normal text-gray-700">
                  Personal Information
                </p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <InputField
                    disabled={isProcessing}
                    variant="auth"
                    extra="shadow-sm"
                    label="First Name"
                    required={true}
                    placeholder="Enter first name"
                    id={`firstname_${subaccount.tempId}`}
                    value={subaccount.firstname}
                    type="text"
                    autoComplete="off"
                    onChange={e => onUpdate(subaccount.tempId, { firstname: e.target.value })}
                  />

                  <InputField
                    disabled={isProcessing}
                    variant="auth"
                    extra="shadow-sm"
                    label="Last Name"
                    required={true}
                    placeholder="Enter last name"
                    id={`lastname_${subaccount.tempId}`}
                    value={subaccount.lastname}
                    type="text"
                    autoComplete="off"
                    onChange={e => onUpdate(subaccount.tempId, { lastname: e.target.value })}
                  />

                  <div>
                    <label className="text-sm font-medium text-gray-800">Dialcode</label>
                    <AutoComplete
                      isDisabled={isDialcodeLoading || isProcessing}
                      placeholder={isDialcodeLoading ? 'Loading...' : 'Please Select'}
                      options={dialcodeOptions}
                      value={dialcodeOptions.find(opt => opt.value === subaccount.mobileDialcode) || null}
                      classNamePrefix="custom-autocomplete"
                      className="autocomplete-override"
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                      onChange={(item: any) =>
                        onUpdate(subaccount.tempId, { mobileDialcode: item?.value || '' })
                      }
                      isClearable
                    />
                  </div>

                  <InputField
                    disabled={isProcessing}
                    variant="auth"
                    extra="shadow-sm"
                    label="Phone"
                    placeholder="Enter mobile number"
                    id={`mobile_${subaccount.tempId}`}
                    value={subaccount.mobile}
                    type="text"
                    autoComplete="off"
                    onChange={e => handleMobileChange(subaccount.tempId, e.target.value)}
                  />

                  <InputField
                    disabled={isProcessing}
                    variant="auth"
                    extra="shadow-sm"
                    label="Email"
                    required={true}
                    placeholder="Enter email"
                    id={`email_${subaccount.tempId}`}
                    value={subaccount.email}
                    type="email"
                    autoComplete="off"
                    onChange={e => handleEmailChange(subaccount.tempId, e.target.value)}
                    error={emailErrors[subaccount.tempId]}
                  />

                  <InputField
                    disabled={isProcessing}
                    variant="auth"
                    extra="shadow-sm"
                    label="Business Name"
                    required={true}
                    placeholder="Enter business name"
                    id={`business_name_${subaccount.tempId}`}
                    value={subaccount.businessName}
                    type="text"
                    autoComplete="off"
                    onChange={e => onUpdate(subaccount.tempId, { businessName: e.target.value })}
                  />
                </div>
              </div>

              {/* Payout Methods Section */}
              <div className="mb-6 border-b border-gray-200 pb-6">
                <PaymentMethodsSelector
                  selectedMethods={subaccount.paymentMethods}
                  onChange={paymentMethods => onUpdate(subaccount.tempId, { paymentMethods })}
                  disabled={isProcessing}
                />
              </div>

              {/* Fee Group Section */}
              <div className="mb-6 border-b border-gray-200 pb-6">
                <FeeGroupSelector
                  value={subaccount.feeGroup}
                  onChange={feeGroup => onUpdate(subaccount.tempId, { feeGroup })}
                  disabled={isProcessing}
                />
              </div>

              {/* API Credentials Section */}
              <div className="mb-6 space-y-4 border-b border-gray-200 pb-6">
                <p className="text-md font-medium tracking-normal text-gray-700">
                  Access Control Credentials
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-navy-700">
                      API Key <span className="text-red-500">*</span>
                    </label>
                    <div className="flex w-full">
                      <input
                        type="text"
                        value={subaccount.auth1}
                        readOnly
                        className="custom-readonly h-10 w-full cursor-default rounded-l-md border-y border-l border-gray-300 bg-gray-50 px-4 text-sm text-gray-700 shadow-sm outline-none"
                      />
                      <div className="flex items-center rounded-r-md border border-gray-300 bg-gray-100 px-1">
                        <Tooltip title="Copy API Key" arrow placement="top">
                          <IconButton
                            size="small"
                            disabled={isProcessing}
                            onClick={() => handleClickCopy(subaccount.auth1)}
                          >
                            <MdOutlineContentCopy className="h-5 w-5 text-navy-800" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Generate API Key" arrow placement="top">
                          <IconButton
                            size="small"
                            disabled={isProcessing}
                            onClick={() => onUpdate(subaccount.tempId, { auth1: generateAPIKey() })}
                          >
                            <TbRefresh className="h-5 w-5 text-navy-800" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-navy-700">
                      API Secret Key <span className="text-red-500">*</span>
                    </label>
                    <div className="flex w-full">
                      <input
                        type="text"
                        value={subaccount.auth2}
                        readOnly
                        className="custom-readonly h-10 w-full cursor-default rounded-l-md border-y border-l border-gray-300 bg-gray-50 px-4 text-sm text-gray-700 shadow-sm outline-none"
                      />
                      <div className="flex items-center rounded-r-md border border-gray-300 bg-gray-100 px-1">
                        <Tooltip
                          title="You will only see the unmask API Secret once, immediately after it's generated."
                          arrow
                          placement="top"
                        >
                          <IconButton size="small">
                            <MdInfoOutline className="h-5 w-5 text-navy-800" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy API Secret" arrow placement="top">
                          <IconButton
                            size="small"
                            disabled={isProcessing}
                            onClick={() => handleClickCopy(subaccount.auth2)}
                          >
                            <MdOutlineContentCopy className="h-5 w-5 text-navy-800" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Generate API Secret" arrow placement="top">
                          <IconButton
                            size="small"
                            disabled={isProcessing}
                            onClick={() => onUpdate(subaccount.tempId, { auth2: generateAPISecret() })}
                          >
                            <TbRefresh className="h-5 w-5 text-navy-800" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Data Privacy Section */}
              <div className="space-y-4">
                <p className="text-md font-medium tracking-normal text-gray-700">
                  Data Privacy
                </p>
                <label className="flex cursor-pointer items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={subaccount.isMasked === 'yes'}
                    onChange={e =>
                      onUpdate(subaccount.tempId, { isMasked: e.target.checked ? 'yes' : 'no' })
                    }
                    disabled={isProcessing}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-navy-700">Mask Account Data</span>
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SubaccountsStep;
